import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DialogueComponent } from '../../features/home/components/dialogue-component/dialogue-component';

@Component({
  selector: 'app-footer',
  imports: [DialogueComponent, CommonModule, RouterModule, FormsModule],
  standalone: true,
  templateUrl: './footer.html',
  styleUrl: './footer.scss'
})

export class Footer implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('stage') stage!: ElementRef<HTMLElement>;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  readonly isBrowser = isPlatformBrowser(this.platformId);

  currentYear: number = new Date().getFullYear();
  newsletterEmail: string = '';
  newsletterSubscribed: boolean = false;

  private iconInterval: number | null = null;
  private pendingTimeouts: number[] = [];
  private visibilityListenerAttached = false;
  private icons: HTMLImageElement[] = [];

  scrollToTop(): void {
    if (this.isBrowser) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  subscribeNewsletter(): void {
    const email = this.newsletterEmail.trim();
    if (email && email.includes('@') && email.includes('.')) {
      this.newsletterSubscribed = true;
      this.newsletterEmail = '';
    }
  }


  // Configuration
  private readonly iconConfig = {
    // Array of icon URLs - add your icon paths here
    iconUrls: [
      '/assets/icons/cinema_icon_popcorn.png',
      '/assets/icons/cinema_icon_reel.png',
      '/assets/icons/cinema_icon_camera.png',
      '/assets/icons/cinema_icon_ticket.png',
      '/assets/icons/cinema_icon_speaker.png',
      '/assets/icons/cinema_icon_frame.png',
      '/assets/icons/cinema_icon_entry.png',
      '/assets/icons/cinema_icon_door.png',
    ],
    // How often to spawn new icons (milliseconds)
    spawnInterval: 2000,
    // Random delay variation
    spawnVariation: 1000,
    // Icon size range
    minSize: 40,
    maxSize: 60,
    // Animation duration range (seconds)
    minDuration: 10,
    maxDuration: 18,
  };

  private readonly onVisibilityChange = (): void => {
    if (this.document.hidden) {
      this.pauseAnimation();
      return;
    }

    this.resumeAnimation();
  };

  ngOnInit(): void {
    if (!this.isBrowser) return;
    this.setupVisibilityListener();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    // Start spawning icons after component is ready
    this.scheduleTimeout(() => {
      this.startIconAnimation();
    }, 500);
  }

  ngOnDestroy(): void {
    this.teardownVisibilityListener();
    this.stopIconAnimation();
    this.clearPendingTimeouts();
    this.clearAllIcons();
  }

  /**
   * Start the icon spawning animation
   */
  private startIconAnimation(): void {
    if (!this.isBrowser || !this.stage?.nativeElement || this.iconInterval !== null) return;

    // Spawn initial batch
    this.spawnIconBatch(3);

    // Set up interval for continuous spawning
    this.iconInterval = window.setInterval(() => {
      this.spawnIcon();
    }, this.iconConfig.spawnInterval);
  }

  /**
   * Stop the icon animation
   */
  private stopIconAnimation(): void {
    if (this.iconInterval !== null) {
      window.clearInterval(this.iconInterval);
      this.iconInterval = null;
    }
  }

  /**
   * Spawn a batch of icons at once
   */
  private spawnIconBatch(count: number): void {
    if (!this.isBrowser) return;

    for (let i = 0; i < count; i++) {
      this.scheduleTimeout(() => {
        this.spawnIcon();
      }, i * 500); // Stagger the spawning
    }
  }

  /**
   * Spawn a single icon
   */
  private spawnIcon(): void {
    if (!this.isBrowser || !this.stage?.nativeElement) return;

    const icon = this.document.createElement('img');
    icon.src = this.getRandomIconUrl();
    icon.classList.add('icon');

    icon.style.left = `${Math.random() * 90}%`;
    icon.style.width = `${this.getRandomSize()}px`;
    icon.style.height = icon.style.width;

    const duration = this.getRandomDuration();
    const delay = Math.random() * 2;
    icon.style.animationDuration = `${duration}s`;
    icon.style.animationDelay = `${delay}s`;

    const maxOpacity = 0.4 + Math.random() * 0.4;
    icon.style.setProperty('--icon-opacity', maxOpacity.toString());

    this.stage.nativeElement.appendChild(icon);
    this.icons.push(icon);

    this.scheduleTimeout(() => this.removeIcon(icon), (duration + delay) * 1000 + 500);
  }


  /**
   * Remove an icon from DOM and array
   */
  private removeIcon(icon: HTMLImageElement): void {
    if (icon.parentNode) {
      icon.parentNode.removeChild(icon);
    }
    
    const index = this.icons.indexOf(icon);
    if (index > -1) {
      this.icons.splice(index, 1);
    }
  }

  /**
   * Clear all icons from the stage
   */
  private clearAllIcons(): void {
    this.icons.forEach(icon => {
      if (icon.parentNode) {
        icon.parentNode.removeChild(icon);
      }
    });
    this.icons = [];
  }

  /**
   * Get a random icon URL from the config
   */
  private getRandomIconUrl(): string {
    const randomIndex = Math.floor(Math.random() * this.iconConfig.iconUrls.length);
    return this.iconConfig.iconUrls[randomIndex] ?? this.iconConfig.iconUrls[0];
  }

  /**
   * Get a random size within the configured range
   */
  private getRandomSize(): number {
    const { minSize, maxSize } = this.iconConfig;
    return Math.floor(Math.random() * (maxSize - minSize + 1)) + minSize;
  }

  /**
   * Get a random duration within the configured range
   */
  private getRandomDuration(): number {
    const { minDuration, maxDuration } = this.iconConfig;
    return Math.random() * (maxDuration - minDuration) + minDuration;
  }

  /**
   * Pause icon animation (useful for performance)
   */
  pauseAnimation(): void {
    if (!this.isBrowser) return;
    this.stopIconAnimation();
  }

  /**
   * Resume icon animation
   */
  resumeAnimation(): void {
    if (!this.isBrowser || this.document.hidden) return;
    this.startIconAnimation();
  }

  /**
   * Check if animations should be paused (e.g., when tab is not visible)
   */
  private setupVisibilityListener(): void {
    if (this.visibilityListenerAttached) return;

    this.document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.visibilityListenerAttached = true;
  }

  private teardownVisibilityListener(): void {
    if (!this.visibilityListenerAttached) return;

    this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.visibilityListenerAttached = false;
  }

  private scheduleTimeout(callback: () => void, delay: number): void {
    const timeoutId = window.setTimeout(() => {
      this.pendingTimeouts = this.pendingTimeouts.filter(
        (pendingTimeout) => pendingTimeout !== timeoutId
      );
      callback();
    }, delay);

    this.pendingTimeouts.push(timeoutId);
  }

  private clearPendingTimeouts(): void {
    this.pendingTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    this.pendingTimeouts = [];
  }
}
